import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollReveal, useScrollRevealMultiple } from '@/hooks/useScrollReveal';
import { Product, allCategoryLabel, fetchPublicProducts, getDisplayPriceRange } from '@/services/products';
import { useCart } from '@/contexts/CartContext';
import { withImageFallback } from '@/utils/images';

function SectionReveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div ref={ref} className={`scroll-reveal ${isVisible ? 'scroll-reveal-visible' : ''} ${delay > 0 ? `scroll-reveal-delay-${Math.min(delay, 6)}` : ''} ${className}`}>
      {children}
    </div>
  );
}

function ProductCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-[#E8E0D6] bg-white">
      <div className="h-56 animate-pulse bg-[#F3EFE8] md:h-64" />
      <div className="p-5 md:p-6">
        <div className="h-3 w-28 animate-pulse rounded-full bg-[#E8E0D6]" />
        <div className="mt-4 h-5 w-3/4 animate-pulse rounded-full bg-[#E8E0D6]" />
        <div className="mt-3 space-y-2">
          <div className="h-3 w-full animate-pulse rounded-full bg-[#EFE8DE]" />
          <div className="h-3 w-2/3 animate-pulse rounded-full bg-[#EFE8DE]" />
        </div>
        <div className="mt-5 flex items-center justify-between border-t border-[#E8E0D6]/60 pt-4">
          <div className="h-7 w-28 animate-pulse rounded-full bg-[#E8E0D6]" />
          <div className="h-9 w-9 animate-pulse rounded-full bg-[#E8E0D6]" />
        </div>
      </div>
    </div>
  );
}

function ProductImage({ product, children }: { product: Product; children?: React.ReactNode }) {
  const [hasImageError, setHasImageError] = useState(false);
  const imageUrl = product.image?.trim();
  const showPlaceholder = !imageUrl || hasImageError;

  return (
    <div className="w-full h-56 md:h-64 overflow-hidden relative bg-[#F7F3ED]">
      {showPlaceholder ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center text-[#9A8A7A]">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
            <i className="ri-image-line text-xl text-[#B9A58F]" />
          </div>
          <span className="text-sm font-semibold">圖片準備中</span>
        </div>
      ) : (
        <img
          src={withImageFallback(imageUrl)}
          alt={product.name}
          onError={() => setHasImageError(true)}
          className="relative z-10 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      )}
      {children}
    </div>
  );
}

function getCategoryParts(category: string) {
  const parts = category.split(">").map((item) => item.trim()).filter(Boolean);
  if (parts.length <= 1) {
    return { primary: category, secondary: "" };
  }
  return {
    primary: parts.at(-1) || category,
    secondary: parts.slice(0, -1).join(" / "),
  };
}

export default function Products() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState(allCategoryLabel);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'price-asc' | 'price-desc' | 'default'>('default');
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [productCategories, setProductCategories] = useState<string[]>([allCategoryLabel]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const { addToCart, openCart } = useCart();
  const [cartFeedback, setCartFeedback] = useState<{ id: number; message: string } | null>(null);
  const categoryCounts = useMemo(() => {
    return allProducts.reduce<Record<string, number>>((counts, product) => {
      counts[product.category] = (counts[product.category] || 0) + 1;
      return counts;
    }, {});
  }, [allProducts]);

  function handleQuickAddToCart(product: Product) {
    if (product.hasVariants) {
      navigate(`/product/${product.slug || product.id}`);
      return;
    }
    const result = addToCart(product, null, 1);
    if (result.ok) {
      openCart();
    } else {
      setCartFeedback({ id: product.id, message: result.message || '無法加入購物車。' });
      window.setTimeout(() => setCartFeedback((current) => (current?.id === product.id ? null : current)), 2500);
    }
  }

  useEffect(() => {
    const urlSearch = searchParams.get('search');
    if (urlSearch) {
      setSearchQuery(urlSearch);
    }
  }, [searchParams]);

  useEffect(() => {
    let alive = true;
    setLoadingProducts(true);
    fetchPublicProducts()
      .then((data) => {
        if (!alive) return;
        setAllProducts(data.products);
        setProductCategories(data.categories);
        if (!data.categories.includes(activeCategory)) {
          setActiveCategory(data.categories[0] || allCategoryLabel);
        }
      })
      .finally(() => {
        if (alive) setLoadingProducts(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = allProducts.filter((p) => {
    const matchCategory = activeCategory === allCategoryLabel || p.category === activeCategory;
    const matchSearch =
      !searchQuery ||
      p.name.includes(searchQuery) ||
      p.subtitle.includes(searchQuery) ||
      p.productCode.includes(searchQuery) ||
      (p.barcode || '').includes(searchQuery) ||
      p.shortDescription.includes(searchQuery) ||
      p.tags.some((t) => t.includes(searchQuery));
    return matchCategory && matchSearch;
  });

  const sorted = [...filtered].sort((a, b) => {
    const aPrice = a.salePrice || a.price;
    const bPrice = b.salePrice || b.price;
    if (sortBy === 'price-asc') return aPrice - bPrice;
    if (sortBy === 'price-desc') return bPrice - aPrice;
    return 0;
  });

  const { setRef, visible } = useScrollRevealMultiple(sorted.length);

  function productTrialBookingPath(product: Product) {
    return `/booking?${new URLSearchParams({
      type: 'ProductTrial',
      productId: String(product.id),
      productName: product.name,
      productCode: product.productCode || '',
    }).toString()}`;
  }

  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-14 md:pt-20">
        {/* Hero */}
        <section className="relative text-white pt-8 md:pt-14 pb-8 md:pb-14">
          {/* Decorative Layer (clipped) */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Solid Dark Gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#0F2A1C] via-[#1B3D2A] to-[#0F2A1C]" />
            {/* Background Image - subtle texture only */}
            <div className="absolute inset-0 opacity-[0.06]">
              <img
                src="https://readdy.ai/api/search-image?query=premium%20modern%20hearing%20aid%20device%20product%20photography%20on%20minimalist%20warm%20cream%20surface%20with%20soft%20natural%20window%20light%20casting%20gentle%20shadows%20professional%20clean%20composition%20green%20botanical%20accent%20subtle%20elegance%20high%20end%20medical%20device%20aesthetic&width=1600&height=700&seq=products-hero-2026&orientation=landscape"
                alt=""
                className="w-full h-full object-cover object-top"
              />
            </div>
            {/* Subtle Geometric Pattern */}
            <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_20%_30%,_#D4A574_1px,_transparent_1px),radial-gradient(circle_at_80%_70%,_#D4A574_1px,_transparent_1px)] bg-[length:60px_60px,80px_80px]" />
            {/* Decorative Glow */}
            <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-[#D4A574]/8 blur-[120px]" />
            <div className="absolute -bottom-40 -left-20 w-[500px] h-[500px] rounded-full bg-[#2E8B57]/12 blur-[100px]" />
          </div>

          <div className="relative px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-6 md:gap-8">
              {/* Left: Title & Description */}
              <div className="max-w-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-px h-5 bg-[#D4A574]" />
                  <span className="text-xs font-semibold tracking-[0.2em] text-[#D4A574] uppercase">
                    Product Series
                  </span>
                </div>
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.25] md:leading-[1.2]">
                  專業助聽器
                  <span className="block text-[#D4A574] mt-2 md:mt-3">產品系列</span>
                </h1>
                <p className="mt-3 md:mt-4 text-white/88 text-base md:text-lg leading-relaxed max-w-xl">
                  富聆聽精選多款專業助聽器，從隱形耳道式到智能藍牙款，
                  滿足不同聽力需求與生活型態，由國家認證聽力師一對一專業選配。
                </p>
                {/* Trust Badges */}
                <div className="mt-4 md:mt-5 flex flex-wrap items-center gap-x-6 gap-y-2">
                  {[
                    { icon: 'ri-shield-check-line', text: '國家認證聽力師' },
                    { icon: 'ri-store-2-line', text: '全台 8 家直營門市' },
                    { icon: 'ri-award-line', text: '15 年專業經驗' },
                  ].map((badge) => (
                    <div key={badge.text} className="flex items-center gap-2 text-white/80 text-xs md:text-sm">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${badge.icon} text-[#D4A574] text-xs`} />
                      </div>
                      <span className="whitespace-nowrap">{badge.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Stats pills */}
              <div className="flex items-center gap-3 md:gap-4 shrink-0">
                <div className="flex flex-col items-center bg-white/8 backdrop-blur-sm rounded-2xl border border-white/10 px-4 py-3 md:px-5 md:py-3.5">
                  <span className="text-2xl md:text-3xl font-bold text-[#D4A574]">10,000+</span>
                  <span className="text-xs text-white/70 mt-1 whitespace-nowrap">服務聽損者</span>
                </div>
                <div className="flex flex-col items-center bg-white/8 backdrop-blur-sm rounded-2xl border border-white/10 px-4 py-3 md:px-5 md:py-3.5">
                  <span className="text-2xl md:text-3xl font-bold text-[#D4A574]">98%</span>
                  <span className="text-xs text-white/70 mt-1 whitespace-nowrap">顧客滿意度</span>
                </div>
                <div className="flex flex-col items-center bg-white/8 backdrop-blur-sm rounded-2xl border border-white/10 px-4 py-3 md:px-5 md:py-3.5">
                  <span className="text-2xl md:text-3xl font-bold text-[#D4A574]">30+</span>
                  <span className="text-xs text-white/70 mt-1 whitespace-nowrap">助聽器型號</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Wave */}
          <svg className="absolute bottom-[-1px] left-0 w-full h-auto block" viewBox="0 0 1440 100" fill="none" preserveAspectRatio="none">
            <path d="M0 100L60 90C120 80 240 60 360 50C480 40 600 38 720 44C840 50 960 62 1080 68C1200 74 1320 74 1380 74L1440 74V100H1380C1320 100 1200 100 1080 100C960 100 840 100 720 100C600 100 480 100 360 100C240 100 120 100 60 100H0Z" fill="white"/>
          </svg>
        </section>

        {/* 篩選與搜尋 */}
        <div className="px-6 md:px-10 lg:px-16 pt-6 md:pt-8 pb-4 md:pb-6 max-w-7xl mx-auto">
          <SectionReveal>
            <div className="rounded-2xl border border-[#E8E0D6] bg-[#FCFAF7]/95 p-3 shadow-sm shadow-[#1B3D2A]/5 md:p-4">
              <div className="flex flex-col gap-4">
              {/* 分類標籤 */}
              <div className="min-w-0 flex-1">
                <div className="-mx-3 overflow-x-auto px-3 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:-mx-4 md:px-4">
                  <div className="flex w-max min-w-full gap-2">
                    {productCategories.map((cat) => {
                      const isActive = activeCategory === cat;
                      const { primary, secondary } = getCategoryParts(cat);
                      const count = cat === allCategoryLabel ? allProducts.length : categoryCounts[cat] || 0;
                      return (
                        <button
                          type="button"
                          key={cat}
                          onClick={() => handleCategoryChange(cat)}
                          title={cat}
                          aria-pressed={isActive}
                          className={`flex min-h-[48px] shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-left transition-all duration-300 md:min-h-[54px] md:px-5 ${
                            isActive
                              ? 'border-[#1B3D2A] bg-[#1B3D2A] text-white shadow-md shadow-[#1B3D2A]/18'
                              : 'border-[#E8E0D6] bg-white text-[#5A6B5A] hover:border-[#D4A574] hover:text-[#1B3D2A] hover:shadow-sm'
                          }`}
                        >
                          <span className="min-w-0">
                            <span className="block max-w-[8.5rem] truncate text-sm font-bold leading-tight md:max-w-[11rem]">
                              {primary}
                            </span>
                            {secondary && (
                              <span className={`mt-0.5 block max-w-[8.5rem] truncate text-[11px] font-medium leading-tight md:max-w-[11rem] ${
                                isActive ? 'text-white/72' : 'text-[#9A8A7A]'
                              }`}>
                                {secondary}
                              </span>
                            )}
                          </span>
                          <span className={`flex h-6 min-w-6 items-center justify-center rounded-full px-2 text-xs font-bold ${
                            isActive ? 'bg-white/18 text-white' : 'bg-[#F4EFE8] text-[#9A8A7A]'
                          }`}>
                            {count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* 搜尋 + 排序 */}
              <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] md:ml-auto md:w-[520px]">
                <div className="relative min-w-0">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                    <i className="ri-search-line text-[#9A8A7A]" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="搜尋產品名稱或功能..."
                    className="h-12 w-full rounded-xl border border-[#E8E0D6] bg-white pl-10 pr-10 text-sm outline-none transition-colors placeholder:text-[#AFA39A] focus:border-[#1B3D2A] focus:ring-2 focus:ring-[#1B3D2A]/15"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-[#9A8A7A] hover:text-[#1B3D2A]"
                    >
                      <i className="ri-close-line" />
                    </button>
                  )}
                </div>
                <div className="relative min-w-[9.5rem]">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                    className="h-12 w-full appearance-none rounded-xl border border-[#E8E0D6] bg-white pl-4 pr-9 text-sm font-semibold text-[#1B3D2A] outline-none transition-colors focus:border-[#1B3D2A] focus:ring-2 focus:ring-[#1B3D2A]/15"
                  >
                    <option value="default">預設排序</option>
                    <option value="price-asc">價格低 → 高</option>
                    <option value="price-desc">價格高 → 低</option>
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#9A8A7A]">
                    <i className="ri-arrow-down-s-line" />
                  </div>
                </div>
              </div>
            </div>
            </div>
          </SectionReveal>
        </div>

        {/* 產品結果統計 */}
        <div className="px-6 md:px-10 lg:px-16 pb-4 max-w-7xl mx-auto">
          <SectionReveal>
            <div className="flex items-center justify-between text-sm text-[#9A8A7A]">
              <span>
                顯示 <span className="font-semibold text-[#1B3D2A]">{sorted.length}</span> 項產品
                {activeCategory !== allCategoryLabel && (
                  <span className="ml-1">· {activeCategory}</span>
                )}
                {searchQuery && (
                  <span className="ml-1">· 「{searchQuery}」</span>
                )}
              </span>
              {sorted.length > 0 && (
                <span className="hidden md:inline">
                  {sortBy === 'price-asc' && '價格由低到高'}
                  {sortBy === 'price-desc' && '價格由高到低'}
                </span>
              )}
            </div>
          </SectionReveal>
        </div>

        {/* 產品列表 */}
        <div data-product-list className="px-6 md:px-10 lg:px-16 pb-12 md:pb-20 max-w-7xl mx-auto">
          {loadingProducts ? (
            <SectionReveal>
              <div>
                <div className="mb-5 flex items-center gap-3 text-sm font-semibold text-[#5A6B5A]">
                  <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-[#D4A574]" />
                  產品資料載入中...
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 lg:grid-cols-3">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <ProductCardSkeleton key={index} />
                  ))}
                </div>
              </div>
            </SectionReveal>
          ) : sorted.length === 0 ? (
            <SectionReveal>
              <div className="text-center py-20 md:py-24">
                <div className="w-20 h-20 rounded-full bg-[#FAF8F5] flex items-center justify-center mx-auto mb-6">
                  <i className="ri-search-line text-3xl text-[#9A8A7A]" />
                </div>
                <p className="text-lg font-semibold text-[#1B3D2A]">沒有符合條件的產品</p>
                <p className="mt-2 text-sm text-[#9A8A7A]">
                  試試不同的搜尋關鍵字或選擇其他分類
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setActiveCategory(allCategoryLabel);
                    setSearchQuery('');
                  }}
                  className="mt-6 bg-[#1B3D2A] text-white px-6 py-2.5 rounded-xl text-sm font-medium hover:bg-[#143026] transition-colors"
                >
                  清除篩選
                </button>
              </div>
            </SectionReveal>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-product-shop>
                {sorted.map((product, i) => {
                  const isNew = product.isNew === true;
                  const priceRange = getDisplayPriceRange(product);
                  const isSale = product.productType === 'Sale';
                  const cardTags = product.tags.length > 0 ? product.tags.slice(0, 3) : product.category.split(">").map((item) => item.trim()).filter(Boolean).slice(-2);
                  return (
                    <div
                      key={product.id}
                      ref={setRef(i)}
                      className={`bg-white rounded-2xl border border-[#E8E0D6] overflow-hidden cursor-pointer hover:border-[#D4A574]/50 hover:shadow-md transition-all duration-300 group scroll-reveal scroll-reveal-delay-${Math.min((i % 3) + 1, 6)} ${visible[i] ? 'scroll-reveal-visible' : ''}`}
                      onClick={() => navigate(`/product/${product.slug || product.id}`)}
                    >
                      {/* 圖片區 */}
                      <ProductImage product={product}>
                        {/* 新品標籤 */}
                        {isNew && (
                          <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-[#D4A574] text-white text-xs font-bold">
                            新品
                          </div>
                        )}
                        {/* 標籤區 */}
                        <div className="absolute bottom-3 left-3 right-3 z-20 flex flex-wrap gap-1.5">
                          {cardTags.map((tag) => (
                            <span
                              key={tag}
                              className="px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm text-xs text-[#5A6B5A] font-medium border border-[#E8E0D6]/50"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </ProductImage>

                      {/* 內容區 */}
                      <div className="p-5 md:p-6">
                        <div className="text-xs font-semibold text-[#D4A574] uppercase tracking-wider mb-1">
                          {product.subtitle}
                        </div>
                        <h3 className="text-lg font-bold text-[#1B3D2A] leading-tight">
                          {product.name}
                        </h3>
                        <p className="mt-2 text-sm text-[#5A6B5A] leading-relaxed line-clamp-2">
                          {product.shortDescription || product.description.replace(/<[^>]*>/g, '')}
                        </p>

                        {/* 底部：價格 + 操作 */}
                        <div className="mt-4 border-t border-[#E8E0D6]/60 pt-4">
                          <div className="mb-3 flex items-center justify-between">
                          <div className="flex items-baseline gap-1">
                            {priceRange.isRange && <span className="text-xs text-[#9A8A7A]">NT$ 起</span>}
                            {!priceRange.isRange && <span className="text-xs text-[#9A8A7A]">NT$</span>}
                            <span className="text-2xl font-bold text-[#1B3D2A]">
                              {priceRange.min.toLocaleString()}
                            </span>
                            {!priceRange.isRange && product.salePrice && <span className="ml-2 text-xs font-semibold text-[#9A8A7A] line-through">NT$ {product.price.toLocaleString()}</span>}
                          </div>
                          <div className="w-9 h-9 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center group-hover:bg-[#1B3D2A] transition-colors duration-300">
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-arrow-right-line text-[#1B3D2A] text-sm group-hover:text-white transition-colors duration-300" />
                            </div>
                          </div>
                          </div>
                          {isSale ? (
                            <div className="flex gap-2">
                              <button
                                type="button"
                                className="flex h-10 flex-1 items-center justify-center gap-2 rounded-xl bg-[#1B3D2A] text-sm font-bold text-white transition-colors hover:bg-[#143026]"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleQuickAddToCart(product);
                                }}
                              >
                                <i className="ri-shopping-cart-2-line" />
                                {product.hasVariants ? '選擇規格' : '加入購物車'}
                              </button>
                              <button
                                type="button"
                                title="預約試戴"
                                aria-label="預約試戴"
                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#E8E0D6] text-[#5A6B5A] transition-colors hover:border-[#1B3D2A] hover:text-[#1B3D2A]"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  navigate(productTrialBookingPath(product));
                                }}
                              >
                                <i className="ri-calendar-check-line" />
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[#1B3D2A] text-sm font-bold text-white transition-colors hover:bg-[#143026]"
                              onClick={(event) => {
                                event.stopPropagation();
                                navigate(productTrialBookingPath(product));
                              }}
                            >
                              <i className="ri-calendar-check-line" />
                              預約試戴
                            </button>
                          )}
                          {cartFeedback?.id === product.id && (
                            <p className="mt-2 text-center text-xs font-medium text-[#c3423f]">{cartFeedback.message}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 底部 CTA */}
              <SectionReveal delay={2}>
                <div className="mt-10 md:mt-16 bg-[#FAF8F5] rounded-2xl border border-[#E8E0D6] p-6 md:p-10 flex flex-col md:flex-row items-stretch md:items-center gap-5 md:gap-8">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center">
                        <i className="ri-customer-service-2-line text-[#1B3D2A]" />
                      </div>
                      <span className="text-xs font-semibold text-[#D4A574] tracking-wider uppercase">專業建議</span>
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">
                      不確定哪款適合您？
                    </h3>
                    <p className="mt-3 text-sm text-[#5A6B5A] leading-relaxed">
                      每個人的聽力狀況不同，建議先進行免費聽力檢測，由專業聽力師為您推薦最適合的機型。
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => document.querySelector('[data-product-list]')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    className="shrink-0 bg-[#1B3D2A] text-white px-6 md:px-8 py-3 rounded-xl text-sm font-bold hover:bg-[#143026] transition-colors whitespace-nowrap flex items-center gap-2"
                  >
                    <i className="ri-calendar-check-line" />
                    選擇商品預約試戴
                  </button>
                </div>
              </SectionReveal>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
